/**
 * Untitled UI Examples Registry
 *
 * Auto-generated metadata for all example pages.
 * Used by the UUI demos gallery for QA review.
 */

export interface ExampleGroup {
  name: string
  description: string
  variants: number | string[] // number for sequential (01, 02...), string[] for named variants
}

export interface ExampleCategory {
  name: string
  groups: Record<string, ExampleGroup>
}

export const examplesRegistry: Record<string, ExampleCategory> = {
  application: {
    name: "Application",
    groups: {
      "dashboards-01": {
        name: "Dashboards Set 1",
        description: "Dashboard layouts with metrics, charts, and data tables",
        variants: 19,
      },
      "dashboards-02": {
        name: "Dashboards Set 2",
        description: "Alternative dashboard layouts and configurations",
        variants: 19,
      },
      "informational-01": {
        name: "Informational Set 1",
        description: "Information pages, help centers, and documentation",
        variants: 19,
      },
      "informational-02": {
        name: "Informational Set 2",
        description: "Alternative informational page layouts",
        variants: 19,
      },
      "settings-01": {
        name: "Settings Set 1",
        description: "Settings pages with forms and preferences",
        variants: 21,
      },
      "settings-02": {
        name: "Settings Set 2",
        description: "Alternative settings page layouts",
        variants: 21,
      },
    },
  },
  marketing: {
    name: "Marketing",
    groups: {
      "landing-pages": {
        name: "Landing Pages",
        description: "Hero sections, feature grids, and call-to-actions",
        variants: 20,
      },
      "pricing-pages": {
        name: "Pricing Pages",
        description: "Pricing tables and plan comparisons",
        variants: 10,
      },
      "about-pages": {
        name: "About Pages",
        description: "Company and team introduction pages",
        variants: 10,
      },
      "team-pages": {
        name: "Team Pages",
        description: "Team member listings and profiles",
        variants: 10,
      },
      "contact-pages": {
        name: "Contact Pages",
        description: "Contact forms and location information",
        variants: 10,
      },
      "faq-pages": {
        name: "FAQ Pages",
        description: "Frequently asked questions and accordions",
        variants: 10,
      },
      "blog-pages": {
        name: "Blog Pages",
        description: "Blog listing pages and archives",
        variants: 10,
      },
      "blog-posts": {
        name: "Blog Posts",
        description: "Individual blog post layouts",
        variants: 10,
      },
      "legal-pages": {
        name: "Legal Pages",
        description: "Terms of service and privacy policies",
        variants: 5,
      },
      "404-pages": {
        name: "404 Pages",
        description: "Error and not found pages",
        variants: 10,
      },
    },
  },
  shared: {
    name: "Shared Pages",
    groups: {
      "login-pages": {
        name: "Login Pages",
        description: "Authentication login page variants",
        variants: [
          "login-simple",
          "login-simple-minimal",
          "login-simple-header-navigation",
          "login-simple-illustration",
          "login-simple-social-logins",
          "login-simple-social-login-leading",
          "login-card-combined",
          "login-card-separated",
          "login-split-image",
          "login-split-carousel",
          "login-split-mockup",
          "login-split-mockup-quote",
          "login-split-quote",
          "login-split-quote-image-01",
          "login-split-quote-image-02",
          "login-split-quote-image-03",
        ],
      },
      "signup-pages": {
        name: "Signup Pages",
        description: "User registration page variants",
        variants: [
          "signup-simple",
          "signup-simple-header-navigation",
          "signup-simple-social-logins",
          "signup-simple-social-login-leading",
          "signup-card-combined",
          "signup-card-separated",
          "signup-progress-01",
          "signup-progress-02",
          "signup-progress-03",
          "signup-sidebar-progress-01",
          "signup-sidebar-progress-02",
          "signup-split-image",
          "signup-split-image-background",
          "signup-split-carousel",
          "signup-split-mockup",
          "signup-split-mockup-quote",
          "signup-split-app-mockup",
          "signup-split-arrow",
          "signup-split-quote-carousel",
          "signup-split-quote-image-01",
          "signup-split-quote-image-02",
        ],
      },
      "forgot-password-pages": {
        name: "Forgot Password",
        description: "Password recovery flow pages",
        variants: [
          "step-1-forgot-password",
          "step-2-check-email",
          "step-3-set-new-password",
          "step-4-success",
          "step-sidebar-version",
        ],
      },
      "verification-pages": {
        name: "Verification",
        description: "Code verification modals",
        variants: ["verification-code", "twofa-code"],
      },
      // Note: email-templates excluded - vendored UUI code has broken imports
      // (references non-existent paths). Raw templates still available at:
      // shared-page-examples/email-templates/
      "email-verification": {
        name: "Email Verification",
        description: "Email verification flow pages",
        variants: [
          "step-1-check-email",
          "step-2-enter-code-manually",
          "step-3-success",
        ],
      },
    },
  },
}

/**
 * Get all examples as a flat array for table display
 */
export interface ExampleEntry {
  category: string
  categoryName: string
  group: string
  groupName: string
  description: string
  variant: number | string // number for sequential, string for named
  variantLabel: string // Display label (e.g., "01" or "login-simple")
  path: string
}

export function getAllExamples(): ExampleEntry[] {
  const entries: ExampleEntry[] = []

  for (const [categoryKey, category] of Object.entries(examplesRegistry)) {
    for (const [groupKey, group] of Object.entries(category.groups)) {
      if (typeof group.variants === "number") {
        // Numeric variants (01, 02, ...)
        for (let variant = 1; variant <= group.variants; variant++) {
          const variantStr = variant.toString().padStart(2, "0")
          entries.push({
            category: categoryKey,
            categoryName: category.name,
            group: groupKey,
            groupName: group.name,
            description: group.description,
            variant,
            variantLabel: variantStr,
            path: `/uui-demos/examples/${categoryKey}/${groupKey}/${variantStr}`,
          })
        }
      } else {
        // Named variants (login-simple, login-card-combined, ...)
        for (const variantName of group.variants) {
          entries.push({
            category: categoryKey,
            categoryName: category.name,
            group: groupKey,
            groupName: group.name,
            description: group.description,
            variant: variantName,
            variantLabel: variantName,
            path: `/uui-demos/examples/${categoryKey}/${groupKey}/${variantName}`,
          })
        }
      }
    }
  }

  return entries
}

/**
 * Get total count of all examples
 */
export function getExampleCount(): number {
  let count = 0
  for (const category of Object.values(examplesRegistry)) {
    for (const group of Object.values(category.groups)) {
      count +=
        typeof group.variants === "number"
          ? group.variants
          : group.variants.length
    }
  }
  return count
}

/**
 * Get variant count for a group (works with both numeric and named variants)
 */
export function getVariantCount(group: ExampleGroup): number {
  return typeof group.variants === "number"
    ? group.variants
    : group.variants.length
}

/**
 * Get variant list for a group (converts numeric to string array)
 */
export function getVariantList(group: ExampleGroup): string[] {
  if (typeof group.variants === "number") {
    return Array.from({ length: group.variants }, (_, i) =>
      (i + 1).toString().padStart(2, "0"),
    )
  }
  return group.variants
}
